import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Navigation, Send } from 'lucide-react';
import { bakeryInfo } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

const ContactPage = () => {
  const { t, currentLang } = useLanguage();
  const isRTL = currentLang === 'ar' || currentLang === 'ku';
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const openGoogleMaps = () => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(bakeryInfo.address)}`, '_blank');
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t.contact?.address || 'العنوان',
      value: bakeryInfo.address,
      isLink: false
    },
    {
      icon: Phone,
      title: t.contact?.phone || 'الهاتف',
      value: bakeryInfo.phone,
      href: `tel:${bakeryInfo.phone}`,
      isLink: true,
      dir: 'ltr'
    },
    {
      icon: Mail,
      title: t.contact?.email || 'البريد الإلكتروني',
      value: bakeryInfo.email,
      href: `mailto:${bakeryInfo.email}`,
      isLink: true,
      dir: 'ltr'
    },
    {
      icon: Clock,
      title: t.contact?.hours || 'ساعات العمل',
      value: t.contact?.hoursValue || 'يومياً: 12:00 AM - 10:00 PM',
      isLink: false
    }
  ];

  const getLabel = (key) => {
    const labels = {
      name: { ar: 'الاسم', de: 'Name', en: 'Name', fr: 'Nom', tr: 'Ad', uk: "Ім'я", ku: 'Nav' },
      message: { ar: 'الرسالة', de: 'Nachricht', en: 'Message', fr: 'Message', tr: 'Mesaj', uk: 'Повідомлення', ku: 'Peyam' },
      send: { ar: 'إرسال', de: 'Senden', en: 'Send', fr: 'Envoyer', tr: 'Gönder', uk: 'Надіслати', ku: 'Bişîne' },
      sendMessage: { ar: 'أرسل لنا رسالة', de: 'Senden Sie uns eine Nachricht', en: 'Send us a message', fr: 'Envoyez-nous un message', tr: 'Bize mesaj gönderin', uk: 'Надішліть нам повідомлення', ku: 'Peyamek ji me re bişînin' },
      success: { ar: 'تم إرسال رسالتك بنجاح!', de: 'Ihre Nachricht wurde erfolgreich gesendet!', en: 'Your message has been sent successfully!', fr: 'Votre message a été envoyé avec succès!', tr: 'Mesajınız başarıyla gönderildi!', uk: 'Ваше повідомлення успішно надіслано!', ku: 'Peyama te bi serkeftî hat şandin!' }
    };
    return labels[key]?.[currentLang] || labels[key]?.en || key;
  };

  return (
    <div className="min-h-screen bg-[#faf8f5] pt-24">
      {/* Page Header */}
      <div className="bg-[#0d4023] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className={`max-w-2xl ${isRTL ? 'ms-auto text-end' : 'me-auto text-start'}`}>
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#b89759] block mb-4">
              {currentLang === 'ar' ? 'تواصل' : currentLang === 'de' ? 'Kontakt' : 'Contact'}
            </span>
            <h1 
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight"
              data-testid="contact-title"
            >
              {t.contact?.title || 'تواصل معنا'}
            </h1>
            <p className="text-lg text-white/70 leading-relaxed">
              {t.contact?.subtitle || 'نحن هنا للإجابة على استفساراتكم وخدمتكم'}
            </p>
          </div>
        </div>
      </div>

      {/* Contact Content */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div className={`space-y-8 ${isRTL ? 'text-end' : 'text-start'}`}>
              <div>
                <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#b89759] block mb-4">
                  {t.footer?.contactUs || 'معلومات الاتصال'}
                </span>
                <h2 className="font-heading text-3xl font-bold text-[#0d4023] tracking-tight">
                  {currentLang === 'ar' ? 'نسعد بتواصلكم' : currentLang === 'de' ? 'Wir freuen uns auf Sie' : 'We look forward to hearing from you'}
                </h2>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                {contactInfo.map((item, i) => (
                  <div 
                    key={i}
                    className={`flex items-start gap-4 p-6 rounded-xl border border-[#b89759]/10 hover:border-[#b89759]/30 bg-white transition-colors duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}
                    data-testid={`contact-info-${i}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-[#0d4023]/5 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#0d4023]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-bold text-[#0d4023] mb-1">{item.title}</h3>
                      {item.isLink ? (
                        <a 
                          href={item.href} 
                          className="text-[#5a7062] hover:text-[#b89759] transition-colors"
                          dir={item.dir}
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-[#5a7062]">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Directions Button */}
              <button
                onClick={openGoogleMaps}
                className={`w-full px-6 py-4 rounded-full bg-[#0d4023] text-[#faf8f5] font-medium flex items-center justify-center gap-3 hover:bg-[#1a5c32] transition-colors duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}
                data-testid="get-directions-btn"
              >
                <Navigation className="w-5 h-5" />
                {t.contact?.getDirections || 'احصل على الاتجاهات'}
              </button>
            </div>

            {/* Contact Form */}
            <div className={`bg-white p-8 lg:p-10 rounded-2xl border border-[#b89759]/10 ${isRTL ? 'text-end' : 'text-start'}`}>
              <h2 className="font-heading text-2xl font-bold text-[#0d4023] mb-6">
                {getLabel('sendMessage')}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">
                    {getLabel('name')}
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none transition-colors bg-[#faf8f5]"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">
                    {t.contact?.email || 'البريد الإلكتروني'}
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none transition-colors bg-[#faf8f5]"
                    dir="ltr"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0d4023] mb-2">
                    {getLabel('message')}
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-[#b89759]/20 focus:border-[#b89759] focus:outline-none transition-colors bg-[#faf8f5] resize-none"
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                <button
                  type="submit"
                  className={`w-full px-6 py-4 rounded-full bg-[#b89759] text-[#0d4023] font-semibold flex items-center justify-center gap-3 hover:bg-[#cba660] transition-colors duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}
                  data-testid="contact-submit-btn"
                >
                  <Send className="w-5 h-5" />
                  {getLabel('send')}
                </button>
                {submitted && (
                  <p className="text-center text-[#0d4023] font-medium bg-[#0d4023]/5 py-3 rounded-xl">
                    {getLabel('success')}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="rounded-2xl overflow-hidden border border-[#b89759]/10 h-80 lg:h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2622.4!2d9.1!3d48.85!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDjCsDUxJzAwLjAiTiA5wrAwNicwMC4wIkU!5e0!3m2!1sen!2sde!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              title="Dura Alsham Location"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
